import { useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import {
  useGetUnitOfMeasurements,
} from "../../api/unit-of-measurement";
import { UnitOfMeasurement } from "../../types/unit-of-measurement";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { StatusBadge } from "../../components/shared/status-badge";
import UnitOfMeasurementModal from "../../components/modals/UnitOfMeasurementModal";
import { ExportButton } from "../../components/ui/ExportButton";
import { useTablePrint } from "../../hooks/useTablePrint";

const ADMIN_ROLES = new Set(["ROLE_KFT_SUPER_ADMIN", "ROLE_SUPER_ADMIN"]);

function UnitOfMeasurementPage() {
  const { user } = useAuth() as AuthContextValue;
  const isAdmin = !!user?.roleName && ADMIN_ROLES.has(user.roleName);

  const [searchText, setSearchText] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [open, setOpen] = useState(false);
  const [selectedUom, setSelectedUom] = useState<UnitOfMeasurement | null>(
    null,
  );

  const { data: uoms = [], isLoading } = useGetUnitOfMeasurements({
    activeOnly: false,
  });
  const { handlePrint } = useTablePrint();

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return uoms;
    return uoms.filter((u) => {
      return (
        (u.code || "").toLowerCase().includes(term) ||
        (u.name || "").toLowerCase().includes(term) ||
        (u.symbol || "").toLowerCase().includes(term) ||
        (u.description || "").toLowerCase().includes(term)
      );
    });
  }, [uoms, searchText]);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const columns = [
    {
      key: "code",
      title: "Code",
      dataIndex: "code",
      align: "left" as const,
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      align: "left" as const,
    },
    {
      key: "symbol",
      title: "Symbol",
      dataIndex: "symbol",
      align: "left" as const,
    },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      align: "left" as const,
      render: (v: string) => v || "-",
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      align: "left" as const,
      render: (v: string) => <StatusBadge value={v} />,
    },
  ];

  const actionColumns = [
    {
      key: "actions",
      dataIndex: "id",
      title: "Actions",
      render: (_: unknown, record: UnitOfMeasurement) => (
        <Button
          variant="default"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedUom(record);
            setOpen(true);
          }}
        >
          <img
            src={Icons.pencilEdit}
            alt="edit"
            className="h-4 w-4 text-gray-100 mr-1"
          />
          Edit
        </Button>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setSelectedUom(null);
    setOpen(true);
  };

  const hideModal = () => {
    setSelectedUom(null);
    setOpen(false);
  };

  const tableColumns = isAdmin ? [...columns, ...actionColumns] : columns;

  return (
    <>
      <div className="hidden md:block rounded-xl bg-white">
        <div className="flex flex-col sm:flex-row justify-between">
          <div className="relative w-full lg:w-64">
            <Input
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by code, name, symbol"
              className="pl-10 rounded-2xl w-[308px] h-11"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <img src={Icons.search} alt="search" className="w-6 h-6" />
            </div>
          </div>

          <div className="flex gap-2">
            <AppButton
              customIcon={Icons.printIcon}
              variant="outline"
              onClick={() =>
                handlePrint(tableColumns, filtered, "Unit of Measurement Report")
              }
              className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            >
              Print
            </AppButton>

            <ExportButton
              columns={tableColumns}
              data={filtered}
              fileName="unit-of-measurement"
              title="Unit of Measurement Report"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: filtered.length,
              }}
              customIcon={Icons.fileExport}
              className="bg-[#DDDDDD] hover:bg-gray-300"
            />

            {isAdmin && (
              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={handleCreate}
              >
                Create New
              </AppButton>
            )}
          </div>
        </div>

        <DataTable<UnitOfMeasurement>
          data={paged}
          columns={tableColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filtered.length,
            onChange: handlePaginationChange,
          }}
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No units of measurement found"
          rowKey="id"
        />
      </div>

      <UnitOfMeasurementModal
        open={open}
        selectedUom={selectedUom}
        hideModal={hideModal}
      />
    </>
  );
}

export default WithAuth(UnitOfMeasurementPage);
